import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getAdminUsersApi, createAdminUserApi, toggleAdminUserApi } from '../../api/adminApi';
import { useSelector } from 'react-redux';

const BLANK = { name: '', email: '', password: '' };

export default function AdminUsersTab() {
  const { user: me }  = useSelector((s) => s.auth);
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(BLANK);
  const [saving, setSaving]   = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const load = () => {
    setLoading(true);
    getAdminUsersApi()
      .then((r) => setAdmins(r.data))
      .catch(() => toast.error('Failed to load admin users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim())              return toast.error('Name is required');
    if (!form.email.trim())             return toast.error('Email is required');
    if (form.password.length < 8)       return toast.error('Password must be at least 8 characters');
    setSaving(true);
    try {
      const r = await createAdminUserApi(form);
      setAdmins((prev) => [...prev, r.data]);
      setModal(false);
      setForm(BLANK);
      toast.success('Admin user created');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Create failed');
    } finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    if (id === me?.id) return toast.error("You can't deactivate yourself");
    try {
      const r = await toggleAdminUserApi(id);
      setAdmins((prev) => prev.map((a) => a.id === id ? r.data : a));
    } catch { toast.error('Toggle failed'); }
  };

  return (
    <div>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Admin Users</h1>
          <p style={S.sub}>{admins.length} administrators</p>
        </div>
        <button style={S.btnAdd} onClick={() => { setForm(BLANK); setModal(true); }}>+ Invite Admin</button>
      </div>

      {/* Info */}
      <div style={S.infoBanner}>
        <span style={{ fontSize: '16px' }}>🔐</span>
        <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}>
          Admin users have full access to this dashboard. Only invite trusted team members.
        </p>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>{['Administrator', 'Email', 'Joined', 'Status', 'Action'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={5} style={S.empty}>Loading...</td></tr>
              : admins.length === 0
                ? <tr><td colSpan={5} style={S.empty}>No admin users found</td></tr>
                : admins.map((a) => (
                  <tr key={a.id} style={S.tr}>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ ...S.avatar, background: a.id === me?.id ? 'var(--red)' : '#111' }}>
                          {a.name?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: 700, color: '#111', fontSize: '13px' }}>{a.name}</p>
                          {a.id === me?.id && <p style={{ margin: 0, fontSize: '10px', color: 'var(--red)', fontWeight: 700 }}>YOU</p>}
                        </div>
                      </div>
                    </td>
                    <td style={S.td}>{a.email}</td>
                    <td style={S.td}>{new Date(a.created_at).toLocaleDateString('en-IN')}</td>
                    <td style={S.td}>
                      <span style={{ ...S.pill, background: a.is_active ? '#DCFCE7' : '#FEE2E2', color: a.is_active ? '#16A34A' : '#DC2626' }}>
                        {a.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={S.td}>
                      {a.id !== me?.id && (
                        <button
                          style={{ ...S.toggleBtn, background: a.is_active ? '#FEF3C7' : '#DCFCE7', color: a.is_active ? '#D97706' : '#16A34A' }}
                          onClick={() => handleToggle(a.id)}
                        >
                          {a.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                      {a.id === me?.id && <span style={{ fontSize: '12px', color: '#9CA3AF' }}>Current session</span>}
                    </td>
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div style={S.overlay} onClick={() => setModal(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}>
              <span style={S.modalTitle}>INVITE ADMIN</span>
              <button style={S.closeBtn} onClick={() => setModal(false)}>✕</button>
            </div>

            <div style={S.formGrid}>
              <div style={S.field}>
                <label style={S.label}>Full Name *</label>
                <input style={S.input} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Rahul Sharma" />
              </div>
              <div style={S.field}>
                <label style={S.label}>Email Address *</label>
                <input style={S.input} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="admin@bhadralok.com" />
              </div>
              <div style={{ ...S.field, gridColumn: '1/-1' }}>
                <label style={S.label}>Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    style={{ ...S.input, paddingRight: '44px' }}
                    type={showPwd ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="Min. 8 characters"
                  />
                  <button style={S.eyeBtn} onClick={() => setShowPwd((p) => !p)} type="button">{showPwd ? '🙈' : '👁'}</button>
                </div>
                <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
                  Use at least 8 characters, including a number and a special character.
                </p>
              </div>
            </div>

            <div style={S.roleInfo}>
              <span style={{ fontWeight: 700, color: '#374151' }}>Permissions:</span> Full dashboard access
              · Orders · Products · Customers · Settings
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button style={S.btnCancel} onClick={() => setModal(false)}>Cancel</button>
              <button style={S.btnSave} onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create Admin'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  hdr:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' },
  title:       { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1.5px', color: '#111', margin: 0 },
  sub:         { fontSize: '13px', color: '#9CA3AF', marginTop: '2px' },
  btnAdd:      { background: '#111', color: '#fff', border: 'none', padding: '10px 20px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '.5px' },
  infoBanner:  { display: 'flex', alignItems: 'center', gap: '10px', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '12px 16px', marginBottom: '16px', fontSize: '13px' },
  tableWrap:   { background: '#fff', border: '1px solid #E5E5E5', overflow: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '10px 16px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', borderBottom: '1px solid #E5E5E5', textAlign: 'left', background: '#FAFAFA' },
  tr:          { borderBottom: '1px solid #F9FAFB', transition: 'background .1s' },
  td:          { padding: '14px 16px', fontSize: '13px', verticalAlign: 'middle' },
  empty:       { textAlign: 'center', padding: '48px', color: '#aaa', fontSize: '14px' },
  avatar:      { width: '36px', height: '36px', borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: '15px', flexShrink: 0 },
  pill:        { display: 'inline-block', fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '99px' },
  toggleBtn:   { border: 'none', padding: '5px 12px', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, cursor: 'pointer' },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  modal:       { background: '#fff', width: '500px', maxWidth: '96vw', padding: '28px' },
  modalHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  modalTitle:  { fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '2px', color: '#111' },
  closeBtn:    { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#666', lineHeight: 1, padding: '4px 8px' },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' },
  field:       { display: 'flex', flexDirection: 'column', gap: '5px' },
  label:       { fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: '#6B7280' },
  input:       { border: '1.5px solid #E5E5E5', padding: '9px 12px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  eyeBtn:      { position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', lineHeight: 1 },
  roleInfo:    { background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '10px 14px', fontSize: '12px', color: '#6B7280', marginBottom: '20px' },
  btnCancel:   { background: '#fff', border: '1.5px solid #E5E5E5', padding: '9px 20px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', color: '#666' },
  btnSave:     { background: '#111', color: '#fff', border: 'none', padding: '10px 22px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '.5px' },
};
